import { useEffect, useRef } from 'react';
import { getClient } from '@matix/api-client';
import { useCart } from '../../store/cart';
import { orderTypeCodeFor } from '../../lib/orderTypeCode';
import { useNetwork } from '../../lib/netStatus';

/**
 * One immediate retry.
 *
 * A quote that never lands is not a cosmetic loss — the server applies the
 * promotion anyway and then refuses a payment sum that does not match it — so
 * a single dropped request would otherwise cost the tenant a sale. Once only,
 * and back to back: the cashier is standing at the counter.
 */
async function withOneRetry<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch {
    return await call();
  }
}

/**
 * Keep the cart's promotion quote in step with its lines.
 *
 * Promotions are priced entirely on the server — the rules, the scoping and
 * the stacking live there, and the till is never trusted with a discount
 * amount. So the only way the cashier can tell a customer what they are saving
 * BEFORE taking payment is to ask.
 *
 * Debounced, because a cashier adding five items would otherwise fire five
 * requests for a basket that only settles once. Cheap either way: the endpoint
 * is read-only — it writes no ledger row and reserves nothing.
 *
 * The quote is a PREVIEW. The sale is priced again on the server when it is
 * charged, and that figure is the one that reaches the books.
 *
 * Every input the server scopes a promotion by has to travel with the basket —
 * order type, customer, delivery fee — and has to be the SAME value the sale
 * will be charged with. A quote asked with a different order type than the
 * charge is worse than no quote: it shows a discount the sale then refuses.
 */
export function usePromotionQuote(): void {
  const lines = useCart((s) => s.lines);
  const customer = useCart((s) => s.customer);
  const orderMode = useCart((s) => s.orderMode);
  const orderType = useCart((s) => s.orderType);
  const deliveryDraft = useCart((s) => s.deliveryDraft);
  const delivery = useCart((s) => s.delivery);
  const setPromotionQuote = useCart((s) => s.setPromotionQuote);
  const setPending = useCart((s) => s.setPromotionQuotePending);
  const online = useNetwork((s) => s.online);

  // The very code the sale will be charged with — see orderTypeCodeFor.
  const orderTypeCode = orderTypeCodeFor(orderMode, orderType);
  // A promotion can be scoped by, or spend itself on, the delivery fee (free
  // delivery), and the fee also raises the basket ceiling a discount is capped
  // at. Mirrors what totals() charges the customer.
  const deliveryFee =
    orderMode === 'delivery' ? deliveryDraft.deliveryFees || 0 : delivery?.fee ?? 0;

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Guards against a slow response for an old basket overwriting a newer one. */
  const seq = useRef(0);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);

    // No network: there is nothing to ask and nothing to wait for.
    //
    // This matters more than it looks. Charge waits on `promotionQuotePending`,
    // so letting the quote run offline would leave the button dead for the
    // length of the timeout after every single basket change — on the one
    // occasion the cashier most needs it to work. An offline sale is charged
    // at menu prices and the server is told to skip the promotion engines
    // (`offline_capture`), so there is no discount to preview either.
    if (!online) {
      setPromotionQuote(null);
      return;
    }

    // Sale lines only. A promotion never applies to the return leg, and asking
    // about a pure refund would be a wasted round trip.
    const saleLines = lines.filter((l) => !l.isReturn);
    if (saleLines.length === 0) {
      setPromotionQuote(null);
      return;
    }

    // From here the cached quote describes an older basket than the one on
    // screen. Say so, so the Charge button waits: the cashier collects the
    // promotion-inclusive total and the server rejects a payment sum that does
    // not match the basket it prices itself.
    setPending(true);

    const mine = ++seq.current;
    timer.current = setTimeout(() => {
      void (async () => {
        try {
          const { data: res } = await withOneRetry(() => getClient().axios.post<{
            available: boolean;
            total_discount: number;
            promotions: Array<{ promotion_id: number; name: string; name_ar?: string | null; discount_amount: number; level?: 'line' | 'order' }>;
            line_discounts?: number[];
            membership?: {
              member_number: string;
              plan_name: string;
              total_discount: number;
              benefits: Array<{ benefit_id: number; name: string; discount_amount: number }>;
            } | null;
          }>(
            '/api/v1/orders/promotions/quote',
            {
              // branch_id omitted on purpose — the JWT's branch is authoritative.
              order_type: orderTypeCode,
              customer_id: customer?.id ?? null,
              delivery_fee: deliveryFee,
              items: saleLines.map((l) => ({
                item_id: l.product.id,
                qty: l.qty,
                total: l.unitPrice * l.qty,
              })),
            },
            // Far below the client's 30 s default, because Charge waits on this
            // answer. A quote nobody gets in two and a half seconds is no use
            // at a counter, and a stalled network must not hold the till.
            { timeout: 2500 },
          ));

          // A newer basket has already been asked about — drop this answer.
          // The newer request owns the pending flag; clearing it here would
          // green-light a Charge against the basket we just superseded.
          if (mine !== seq.current) return;

          // A membership benefit is the same KIND of thing as a promotion from
          // the cart's point of view — a header discount the server priced and
          // the cashier cannot decline — so it rides in the same rows rather
          // than in a parallel total. Prefixed with the plan so the cashier can
          // say WHY it came off: "Gold · Free coffee", not a bare "Free coffee"
          // the customer might think is a mistake.
          const member = res.membership;
          const memberRows = (member?.benefits ?? []).map((b) => ({
            name: `${member!.plan_name} · ${b.name}`,
            amount: b.discount_amount,
          }));
          const total = res.total_discount + (member?.total_discount ?? 0);

          setPromotionQuote(
            total > 0
              ? {
                  totalDiscount: total,
                  promotions: [
                    ...res.promotions.map((p) => ({
                      name: p.name,
                      nameAr: p.name_ar ?? null,
                      amount: p.discount_amount,
                      ...(p.level ? { level: p.level } : {}),
                    })),
                    ...memberRows,
                  ],
                  ...(res.line_discounts ? { lineDiscounts: res.line_discounts } : {}),
                }
              : null,
          );
        } catch {
          // Give up rather than hold the till: the cart falls back to menu
          // prices and Charge is released.
          //
          // Note this is NOT harmless when a promotion really does apply. The
          // server prices it regardless and then requires the payment sum to
          // match ITS net, so a sale rung up at menu prices is refused with
          // PAYMENT_SUM_MISMATCH. That is why the call retries once above, and
          // why the quote is closer to load-bearing than to a nicety.
          if (mine === seq.current) setPromotionQuote(null);
        }
      })();
    }, 300);

    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [lines, orderTypeCode, deliveryFee, customer, online, setPromotionQuote, setPending]);
}
